// components/FloatingWindow.js
import React, { useState } from 'react';
import { 
  Box, 
  IconButton, 
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  CropSquare as CropSquareIcon,
  FilterNone as FilterNoneIcon
} from '@mui/icons-material';

const FloatingWindow = ({ 
  title, 
  children, 
  open, 
  onClose, 
  onMinimize,
  initialPosition = { x: 100, y: 100 },
  initialSize = { width: 800, height: 700 }
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [originalState, setOriginalState] = useState(null);

  const handleMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMaximize = () => {
    if (isMaximized) {
      setPosition(originalState.position);
      setSize(originalState.size);
    } else {
      setOriginalState({ position, size });
      setPosition({ x: 0, y: 0 });
      setSize({ 
        width: window.innerWidth, 
        height: window.innerHeight 
      });
    }
    setIsMaximized(!isMaximized);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    if (onMinimize) onMinimize();
  };

  if (!open) return null;
  if (isMinimized) return null;

  return (
    <Box
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      sx={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        backgroundColor: 'background.paper',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: 24,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300, // Z-index maior que o padrão do Material-UI (1200)
        overflow: 'hidden',
        resize: isMaximized ? 'none' : 'both',
        minWidth: '400px',
        minHeight: '300px',
        cursor: isDragging ? 'grabbing' : 'default'
      }}
    >
      {/* Barra de título */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: '#3FA5D4',
          color: 'white',
          cursor: isDragging ? 'grabbing' : 'move'
        }}
      >
        <Typography variant="subtitle1">{title}</Typography>
        <Box>
          <IconButton 
            size="small" 
            onClick={handleMinimize}
            sx={{ color: 'white' }}
            className="no-drag"
          >
            <MinimizeIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={handleMaximize}
            sx={{ color: 'white' }}
            className="no-drag"
          >
            {isMaximized ? (
              <FilterNoneIcon fontSize="small" />
            ) : (
              <CropSquareIcon fontSize="small" />
            )}
          </IconButton>
          <IconButton 
            size="small" 
            onClick={onClose}
            sx={{ color: 'white' }}
            className="no-drag"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Conteúdo */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        padding: 2,
        zIndex: 1301 // Garante que o conteúdo esteja acima da janela
      }}>
        {children}
      </Box>
    </Box>
  );
};

export default FloatingWindow;